import React, { useCallback, useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";

import { Log } from "../../../core/entities/log";
import {
  fetchApplicantRequest,
  updateApplicantRequest,
  resetIsUpdateSuccessful,
  resetApplicantState,
  updateTotalNewApplicants,
  clearApplicantValues,
  uploadApplicantDocuments,
  resetIsOverlap,
  checkScheduleRequest,
  deleteApplicantDocuments,
} from "../../../redux/applicant/actions";
import {
  fetchComments,
  updateCommentRequest,
} from "../../../redux/comment/actions";
import { ApplicantDocument, FirebaseApplicant } from "../../../core/entities/applicant";
import ApplicantDetailsPage from "./ApplicantDetailsPage";
import { getLogs, updateLog } from "../../../redux/logs/actions";
import { clearSpecificRequest } from "../../../redux/request/action";
import {
  FETCH_APPLICANTS_REQUEST,
  UPDATE_APPLICANT_REQUEST,
} from "../../../redux/applicant/actionTypes";
import { Comment } from "../../../core/entities/comment";
import { UPDATE_LOGS_REQUEST } from "../../../redux/logs/actionTypes";
import { Notification } from "../../../core/entities/notification";
import { addNotificationRequest } from "../../../redux/notification/actions";
import { clearUserValues, searchUsers } from "../../../redux/user/action";
import {
  clearInterviewers,
  fetchInterviewersRequest,
} from "../../../redux/interviewer/actions";
import { downloadFileRequest } from "../../../redux/storage/actions";

const ApplicantDetailsContainer = () => {
  const isLoading = useSelector(
    (state: RootStateOrAny) => state.applicant.isLoading
  );

  const isLoadingFile = useSelector(
    (state: RootStateOrAny) => state.storage.isLoading
  );

  const schedule = useSelector((state: RootStateOrAny) => state.applicant);

  const isUploadApplicantDocumentsLoading = useSelector(
    (state: RootStateOrAny) => state.applicant.isUploadApplicantDocumentsLoading
  );

  const isSearchUsersLoading = useSelector(
    (state: RootStateOrAny) => state.user.isSearching
  );
  const applicant = useSelector(
    (state: RootStateOrAny) => state.applicant.applicant
  );
  const isUpdating = useSelector(
    (state: RootStateOrAny) => state.applicant.isUpdating
  );
  const error = useSelector((state: RootStateOrAny) => state.applicant.error);
  const isUpdateSuccessful = useSelector(
    (state: RootStateOrAny) => state.applicant.isUpdateSuccessful
  );
  const user = useSelector((state: RootStateOrAny) => state.auth.user);
  const comments = useSelector(
    (state: RootStateOrAny) => state.comment.commentsDoc
  );
  const isCommentLoading = useSelector(
    (state: RootStateOrAny) => state.comment.isLoading
  );
  const logs = useSelector((state: RootStateOrAny) => state.logs.logs);

  const interviewers = useSelector(
    (state: RootStateOrAny) => state.interviewer.interviewers
  );

  const updateLogsRequest = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[UPDATE_LOGS_REQUEST]
  );

  const updateApplicantRequestState = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[UPDATE_APPLICANT_REQUEST]
  );

  const isUploadApplicantDocumentsSuccessful = useSelector(
    (state: RootStateOrAny) => state.applicant.isUploadApplicantDocumentsSuccessful
  );

  const isDeleteApplicantDocumentsLoading = useSelector(
    (state: RootStateOrAny) => state.applicant.isDeleteApplicantDocumentsLoading
  );

  const isDeleteApplicantDocumentsSuccessful = useSelector(
    (state: RootStateOrAny) => state.applicant.isDeleteApplicantDocumentsSuccessful
  );

  const dispatch = useDispatch();
  const dispatchFetchUser = useCallback(
    (id: string) => dispatch(fetchApplicantRequest(id)),
    [dispatch]
  );
  const dispatchUpdateApplicant = useCallback(
    (id: string, applicantStatus: Partial<FirebaseApplicant>) =>
      dispatch(updateApplicantRequest(id, applicantStatus)),
    [dispatch]
  );
  const dispatchResetIsUpdateSuccessful = useCallback(() => {
    dispatch(resetIsUpdateSuccessful());
    dispatch(clearSpecificRequest(FETCH_APPLICANTS_REQUEST));
    dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST));
  }, [dispatch]);
  const dispatchUpdateLogs = useCallback(
    (applicantId: string, data: Log) => {
      dispatch(clearSpecificRequest(UPDATE_LOGS_REQUEST));
      dispatch(updateLog(applicantId, data));
    },
    [dispatch]
  );
  const dispatchFetchComments = useCallback(
    (applicantId: string) => dispatch(fetchComments(applicantId)),
    [dispatch]
  );
  const dispatchUpdateComments = useCallback(
    (applicantId: string, id: string, comments: Comment[]) =>
      dispatch(updateCommentRequest(applicantId, id, comments)),
    [dispatch]
  );

  const dispatchGetLogs = useCallback(
    (applicantId: string) => dispatch(getLogs(applicantId)),
    [dispatch]
  );

  const dispatchSearchUsers = useCallback(
    (name: string) => dispatch(searchUsers(name)),
    [dispatch]
  );

  const dispatchUpdateTotalNewApplicants = useCallback(
    (currentPhase: string, prevPhase: string, currentStatus: string) =>
      dispatch(
        updateTotalNewApplicants(currentPhase, prevPhase, currentStatus)
      ),
    [dispatch]
  );

  const dispatchResetIsOverlap = useCallback(
    () => dispatch(resetIsOverlap()),
    [dispatch]
  );

  const dispatchCheckSchedule = useCallback(
    (id: string, dateAndTime: string, params: Partial<FirebaseApplicant>) =>
      dispatch(checkScheduleRequest(id, dateAndTime, params)),
    [dispatch]
  );

  const clearDispatchUpdateApplicantRequest = useCallback(
    () => dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST)),
    [dispatch]
  );

  const dispatchAddNotification = useCallback(
    (notification: Notification) =>
      dispatch(addNotificationRequest(notification)),
    [dispatch]
  );

  const dispatchDownloadDocument = useCallback(
    (fileUrl: string, customFileName?: string) => dispatch(downloadFileRequest(fileUrl, customFileName)),
    [dispatch]
  );

  const dispatchUpdateApplicantDocuments = useCallback(
    (id: string, file: Blob, fileName: string) => dispatch(uploadApplicantDocuments(id, file, fileName)),  
    [dispatch]
  );

  const dispatchDeleteApplicantDocuments = useCallback(
    (id: string, document: ApplicantDocument) => dispatch(deleteApplicantDocuments(id, document)),
    [dispatch]
  );


  useEffect(() => {
    dispatch(fetchInterviewersRequest());

    return () => {
      dispatch(clearSpecificRequest(UPDATE_LOGS_REQUEST));
      dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST));
      dispatch(clearUserValues());
      dispatch(clearApplicantValues());
      dispatch(resetApplicantState());
      dispatch(clearInterviewers());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredInterviewers = interviewers.filter((value: any) => value.email !== 'hrph@vananaz.com');

  return (
    <ApplicantDetailsPage
      isLoadingFile={isLoadingFile}
      isLoading={isLoading}
      isOverlap={schedule?.isOverlap}
      isChecking={schedule?.isChecking}
      resetIsOverlap={dispatchResetIsOverlap}
      checkSchedule={dispatchCheckSchedule}
      applicant={applicant}
      fetchApplicant={dispatchFetchUser}
      updateApplicant={dispatchUpdateApplicant}
      isUpdating={isUpdating}
      error={error}
      isUpdateSuccessful={isUpdateSuccessful}
      resetIsUpdateSuccessful={dispatchResetIsUpdateSuccessful}
      user={user}
      updateLogs={dispatchUpdateLogs}
      fetchComments={dispatchFetchComments}
      comments={comments}
      isCommentsLoading={isCommentLoading}
      updateComments={dispatchUpdateComments}
      getLogs={dispatchGetLogs}
      logs={logs}
      updateLogsRequest={updateLogsRequest}
      updateApplicantRequest={updateApplicantRequestState}
      updateTotalNewApplicants={dispatchUpdateTotalNewApplicants}
      addNotification={dispatchAddNotification}
      interviewers={filteredInterviewers}
      clearApplicantRequest={clearDispatchUpdateApplicantRequest}
      fetchUsers={dispatchSearchUsers}
      isSearchUsersLoading={isSearchUsersLoading}
      downloadDocument={dispatchDownloadDocument}
      uploadApplicantDocument={dispatchUpdateApplicantDocuments}
      isUploadApplicantDocumentsLoading={isUploadApplicantDocumentsLoading}
      isUploadApplicantDocumentsSuccessful={isUploadApplicantDocumentsSuccessful}
      deleteApplicantDocuments={dispatchDeleteApplicantDocuments}
      isDeleteApplicantDocumentsLoading={isDeleteApplicantDocumentsLoading}
      isDeleteApplicantDocumentsSuccessful={isDeleteApplicantDocumentsSuccessful}
    />
  );
};

export default ApplicantDetailsContainer;
